import { GameConfig, RouteName } from "../config/GameConfig";
import { GameNavigator } from "./Navigation";
import { GameStore } from "./GameStore";
import {
    ReferenceHomeScreen,
    ReferenceLevelScreen,
    ReferenceQuizScreen,
    ReferenceRankScreen,
    ReferenceWalletScreen,
} from "../scenes/ReferenceScreens";
import { BaseScreen } from "../scenes/BaseScreen";
import { UIFactory } from "../ui/UIFactory";
import { ReferenceUI } from "../ui/ReferenceUI";
import { AdFarmSession } from "../integration/AdFarmSession";
import { AuthScreen } from "../scenes/AuthScreen";
import { MineScreen, TaskScreen } from "../scenes/AccountScreens";
import { FestiveMotion } from "../ui/FestiveMotion";
import { GameAudio } from "../audio/GameAudio";

export class GameApp extends Laya.Box implements GameNavigator {
    private currentScreen: BaseScreen;
    private toastView: Laya.Box;

    constructor() {
        super();
        this.width = GameConfig.DESIGN_WIDTH;
        this.height = Math.max(GameConfig.DESIGN_HEIGHT, Laya.stage.height || GameConfig.DESIGN_HEIGHT);
        Laya.stage.on(Laya.Event.CLICK, this, this.unlockAudio);
        this.bootstrap();
    }

    private async bootstrap(): Promise<void> {
        try {
            await ReferenceUI.preload();
            const session = AdFarmSession.instance;
            session.on(AdFarmSession.AUTH_CHANGED, this, this.onAuthChanged);
            session.api.onForbidden = message => this.toast(message);
            await session.restore();
            if (session.authenticated) {
                await session.rewards.resumeRewarded(async () => session.refreshOverview());
            }
            this.navigate(session.authenticated ? "home" : "auth");
        } catch (error) {
            console.error(error);
            this.toast("UI 资源加载失败，请刷新重试");
        }
    }

    navigate(route: RouteName): void {
        if (route !== "auth" && !AdFarmSession.instance.authenticated) route = "auth";
        if (route === "quiz" && !GameStore.instance.spendEnergy()) {
            this.toast("体力不足，点击首页右上角“+”补充体力");
            return;
        }

        let next: BaseScreen;
        switch (route) {
            case "auth": next = new AuthScreen(this); break;
            case "tasks": next = new TaskScreen(this); break;
            case "mine": next = new MineScreen(this); break;
            case "levels": next = new ReferenceLevelScreen(this); break;
            case "quiz": next = new ReferenceQuizScreen(this); break;
            case "wallet": next = new ReferenceWalletScreen(this); break;
            case "rank": next = new ReferenceRankScreen(this); break;
            default: next = new ReferenceHomeScreen(this); break;
        }
        if (this.currentScreen) {
            Laya.Tween.clearAll(this.currentScreen);
            this.currentScreen.removeSelf();
            this.currentScreen.destroy(true);
        }
        this.currentScreen = next;
        GameAudio.setRoute(route);
        next.addChild(new FestiveMotion(route));
        next.alpha = 0;
        next.x = route === "home" || route === "auth" ? -28 : 28;
        this.addChild(next);
        Laya.Tween.to(next, { alpha: 1, x: 0 }, 260, Laya.Ease.quadOut);
    }

    destroy(destroyChild = true): void {
        AdFarmSession.instance.off(AdFarmSession.AUTH_CHANGED, this, this.onAuthChanged);
        Laya.stage.off(Laya.Event.CLICK, this, this.unlockAudio);
        super.destroy(destroyChild);
    }

    private unlockAudio(): void {
        GameAudio.unlock();
    }

    private onAuthChanged(authenticated: boolean): void {
        if (!authenticated) {
            this.toast("登录状态已失效，请重新登录");
            this.navigate("auth");
        }
    }

    toast(message: string): void {
        if (this.toastView) {
            Laya.Tween.clearAll(this.toastView);
            this.toastView.destroy(true);
        }
        const box = new Laya.Box();
        box.width = 620;
        box.height = 86;
        box.mouseEnabled = false;
        box.pos(65, Math.min(this.height - 190, 1370));
        UIFactory.roundRect(box, 0, 0, 620, 82, 35, "#241A17E8", "#F0C36F", 3);
        const label = UIFactory.label(message, 24, "#FFF3D0", 570, 70, true);
        label.pos(25, 6);
        label.wordWrap = true;
        box.addChild(label);
        this.toastView = box;
        this.addChild(box);
        box.alpha = 0;
        box.y += 20;
        Laya.Tween.to(box, { alpha: 1, y: box.y - 20 }, 180, Laya.Ease.quadOut, Laya.Handler.create(this, () => {
            Laya.timer.once(1650, box, () => {
                Laya.Tween.to(box, { alpha: 0, y: box.y - 15 }, 260, Laya.Ease.quadIn, Laya.Handler.create(box, () => box.destroy(true)));
            });
        }));
    }
}
